import React, { useState, useEffect, useMemo } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell
} from 'recharts';
import {
  Search, Download, AlertTriangle, CheckCircle, XCircle, Clock,
  Shield
} from 'lucide-react';
import { ApiNames } from 'components/Common/ApiManagement/apiServices';
import { PAGEPATHS } from 'components/Common/Constant';
import '../assets/css/dashboard.css';

const GovComplianceDashboard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { targetId } = useParams();

  const [loading, setLoading] = useState(true);
  const [allFrameworks, setAllFrameworks] = useState([]);
  const [selectedFrameworkId, setSelectedFrameworkId] = useState('all');
  const [frameworkData, setFrameworkData] = useState({});
  const [controlsData, setControlsData] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
const [severityFilter, setSeverityFilter] = useState("All");
  // Fetch all frameworks ONCE
  useEffect(() => {
    const fetchAllFrameworks = async () => {
      if (!targetId) return;
      try {
        setLoading(true);
        const res = await fetch(ApiNames.FETCH_ALL_FRAMEWORKS(targetId));
        const result = await res.json();
        if (result.success && result.data) {
          setAllFrameworks(result.data);
        } else {
          setAllFrameworks([]);
        }
      } catch (err) {
        console.error('Error fetching all frameworks:', err);
        setAllFrameworks([]);
      } finally {
        setLoading(false);
      }
    };

    fetchAllFrameworks();
  }, [targetId]);

  // Handle selection change (aggregate or pick one)
  useEffect(() => {
    if (!targetId || allFrameworks.length === 0) return;

    if (selectedFrameworkId === 'all') {
      // Aggregate all frameworks
      const allControls = allFrameworks.flatMap(f =>
        (f.controls || []).map(c => ({
          ...c,
          frameworkName: f.framework.name
        }))
      );

      const summary = allFrameworks.reduce(
        (acc, f) => {
          acc.compliant += f.framework.summary.compliant;
          acc.nonCompliant += f.framework.summary.nonCompliant;
          acc.partiallyCompliant += f.framework.summary.partiallyCompliant;
          acc.underReview += f.framework.summary.underReview;
          acc.critical += f.framework.summary.criticalIssues;
          return acc;
        },
        { compliant: 0, nonCompliant: 0, partiallyCompliant: 0, underReview: 0, critical: 0 }
      );

      const avgScore = allFrameworks.length
        ? Math.round(allFrameworks.reduce((sum, f) => sum + f.framework.avgScore, 0) / allFrameworks.length)
        : 0;
      const passRate = allFrameworks.length
        ? Math.round(allFrameworks.reduce((sum, f) => sum + f.framework.passRate, 0) / allFrameworks.length)
        : 0;

      setFrameworkData({
        name: 'All Frameworks',
        description: '',
        summary,
        passRate,
        avgScore
      });
      setControlsData(allControls);
    } else {
      // Pick a single framework
      const fw = allFrameworks.find(f => f.frameworkId === selectedFrameworkId);
      if (fw) {
        const controlsWithFramework = (fw.controls || []).map(c => ({
          ...c,
          frameworkName: fw.framework.name
        }));
        setControlsData(controlsWithFramework);
        setFrameworkData(fw.framework);
      }
    }
  }, [selectedFrameworkId, allFrameworks, targetId]);
const frameworkChartData = useMemo(() => {
  if (!allFrameworks || allFrameworks.length === 0) return [];

  return allFrameworks.map(fw => ({
    name: fw.framework.name,   // short label
    fullName: fw.framework.description || fw.framework.name, // full name for tooltip
    compliant: fw.framework.summary?.compliant || 0,
    partial: fw.framework.summary?.partiallyCompliant || 0,
    nonCompliant: fw.framework.summary?.nonCompliant || 0,
  }));
}, [allFrameworks]);
console.log("All frameworks:", allFrameworks);

  // Filter controls by search
 const filteredControls = useMemo(() => {
  return controlsData.filter(control => {
    const matchesSearch =
      control.controlName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      control.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      control.scanner?.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesSeverity =
      severityFilter === "All" || control.priority === severityFilter;

    return matchesSearch && matchesSeverity;
  });
}, [controlsData, searchTerm, severityFilter]);

  // Metrics
  const metrics = useMemo(() => {
    if (!frameworkData?.summary) return {};
    return {
      compliant: frameworkData.summary.compliant || 0,
      nonCompliant: frameworkData.summary.nonCompliant || 0,
      partiallyCompliant: frameworkData.summary.partiallyCompliant || 0,
      underReview: frameworkData.summary.underReview || 0,
      critical: frameworkData.summary.critical || 0,
      complianceRate: frameworkData.passRate || 0,
      avgScore: frameworkData.avgScore || 0
    };
  }, [frameworkData]);

  // Pie chart data
  const statusData = [
    { name: 'Compliant', value: metrics.compliant || 0, color: '#10B981' },
    { name: 'Non-Compliant', value: metrics.nonCompliant || 0, color: '#EF4444' },
    { name: 'Partially Compliant', value: metrics.partiallyCompliant || 0, color: '#F59E0B' },
    { name: 'Under Review', value: metrics.underReview || 0, color: '#6B7280' }
  ];

  // Bar chart severity
 const severityData = useMemo(() => {
  const sevStats = { Critical: 0, High: 0, Medium: 0, Low: 0 };
  filteredControls.forEach(c => {
    if (c.priority) sevStats[c.priority] = (sevStats[c.priority] || 0) + 1;
  });
  return [
    { name: 'Critical', value: sevStats.Critical, color: '#DC2626' },
    { name: 'High', value: sevStats.High, color: '#EA580C' },
    { name: 'Medium', value: sevStats.Medium, color: '#D97706' },
    { name: 'Low', value: sevStats.Low, color: '#65A30D' }
  ];
}, [filteredControls]);

  const getStatusIcon = (status) => {
    switch (status) {
      case 'Compliant': return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'Non-Compliant': return <XCircle className="h-4 w-4 text-red-500" />;
      case 'Partially Compliant': return <AlertTriangle className="h-4 w-4 text-yellow-500" />;
      case 'Under Review': return <Clock className="h-4 w-4 text-gray-500" />;
      default: return <Clock className="h-4 w-4 text-gray-500" />;
    }
  };

  if (loading) return <div className="p-6">Loading frameworks...</div>;

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        
        <div className="mb-8 flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
              <Shield className="h-8 w-8 text-blue-600" />
              Government Compliance Dashboard
            </h1>
            <p className="text-gray-600 mt-2">{frameworkData?.description || ''}</p>
          </div>
          <div className="flex items-center gap-4">
            <button className="bg-blue-600 text-white px-4 py-2 rounded-lg flex items-center gap-2 hover:bg-blue-700">
              <Download className="h-4 w-4" /> Export Report
            </button>
            {selectedFrameworkId !== 'all' && (
              <button
                className="bg-blue-600 text-white px-4 py-2 rounded-lg flex items-center gap-2 hover:bg-blue-700"
                onClick={() =>
                  navigate(`${PAGEPATHS.FrameworkDetail}/${selectedFrameworkId}?target=${targetId}`)
                }
              >
                View Details
              </button>
            )}
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-lg shadow p-6 mb-8">
          <div className="grid grid-cols-1 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Search</label>
              <div className="relative">
                <Search className="h-4 w-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search controls..."
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Framework</label>
              <select
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                value={selectedFrameworkId}
                onChange={(e) => setSelectedFrameworkId(e.target.value)}
              >
                <option value="all">All Frameworks</option>
                {allFrameworks.map(f => (
                  <option key={f.frameworkId} value={f.frameworkId}>
                    {f.framework.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
  <label className="block text-sm font-medium text-gray-700 mb-2">Severity</label>
  <select
    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
    value={severityFilter}
    onChange={(e) => setSeverityFilter(e.target.value)}
  >
    <option>All</option>
    <option>Critical</option>
    <option>High</option>
    <option>Medium</option>
    <option>Low</option>
  </select>
</div>
             <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Time Range</label>
              <select
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                
              >
                <option value="7d">Last 7 days</option>
                <option value="30d">Last 30 days</option>
                <option value="90d">Last 90 days</option>
                <option value="1y">Last year</option>
              </select>
            </div>
          </div>
        </div>

        {/* Metrics */}
        <div className="grid grid-cols-1 gap-6 mb-8">
          <div className="bg-white rounded-lg shadow p-6">
            <p className="text-sm font-medium text-gray-600">Compliance Rate</p>
            <p className="text-3xl font-bold text-green-600">{metrics.complianceRate || 0}%</p>
            <p className="text-sm text-gray-500 mt-2">{metrics.compliant} of {controlsData.length} controls compliant</p>
          </div>
          <div className="bg-white rounded-lg shadow p-6">
            <p className="text-sm font-medium text-gray-600">Critical Issues</p>
            <p className="text-3xl font-bold text-red-600">{metrics.critical || 0}</p>
            <p className="text-sm text-gray-500 mt-2">High priority issues</p>
          </div>
          <div className="bg-white rounded-lg shadow p-6">
            <p className="text-sm font-medium text-gray-600">Average Score</p>
            <p className="text-3xl font-bold text-blue-600">{metrics.avgScore || 0}</p>
          </div>
        </div>
 <div className="grid grid-cols-1 gap-8 mb-8">
          {/* Compliance Status Distribution */}
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Compliance Status Distribution</h3>
            <div className="h-64">
              <ResponsiveContainer width="100%" height={320}>
              <PieChart>
                <Pie
                  data={statusData}
                  dataKey="value"
                  nameKey="name"
                  innerRadius={60}
                    outerRadius={100}
                    paddingAngle={5}
                  // fill="#8884d8"
                  label
                >
                  {statusData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
              </PieChart>
              {/* <Tooltip /> */}
            </ResponsiveContainer>
          </div> 
          <div className="grid grid-cols-2 gap-4 mt-4">
              {statusData.map((item, index) => (
                <div key={index} className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full" style={{ backgroundColor: item.color }}></div>
                  <span className="text-sm text-gray-600">{item.name}: {item.value}</span>
                </div>
              ))}
            </div>
          </div>
        {/* Charts */}
        <div className="grid grid-cols-1 gap-6 mb-8">
           <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Issue Severity Distribution</h3>
            <div className="h-64">
              <ResponsiveContainer width="100%" height={320}>
                <BarChart data={severityData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis />
                  <Tooltip />
                  <Bar dataKey="value" fill="#3B82F6" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-6 mb-8">
  <h3 className="text-lg font-semibold text-gray-900 mb-4">Compliance by Framework</h3>
  <div className="h-96">
    <ResponsiveContainer width="100%" height={320}>
      <BarChart data={frameworkChartData} margin={{ top: 20, right: 30, left: 20, bottom: 60 }}>
        <CartesianGrid strokeDasharray="3 3" />
        <XAxis 
          dataKey="name" 
          angle={-45}
          textAnchor="end"
          height={80}
          interval={0}
        />
        <YAxis />
        <Tooltip 
          formatter={(value, name) => [`${value}`, name]}
          labelFormatter={(label) => {
            const item = frameworkChartData.find(d => d.name === label);
            return item ? item.fullName : label;
          }}
        />
        <Bar dataKey="compliant" stackId="a" fill="#10B981" name="Compliant" />
        <Bar dataKey="partial" stackId="a" fill="#F59E0B" name="Partially Compliant" />
        <Bar dataKey="nonCompliant" stackId="a" fill="#EF4444" name="Non-Compliant" />
      </BarChart>
    </ResponsiveContainer>
  </div>
</div>
          {/* <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Severity Distribution</h3>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={severityData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis allowDecimals={false} />
                <Tooltip />
                <Bar dataKey="value" fill="#8884d8">
                  {severityData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div> */}
        </div>

        {/* Controls Table */}
        <div className="bg-white rounded-lg shadow p-6 mb-8 overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Framework</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Category</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Control Name</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Scanner</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Severity</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Score</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Violations</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredControls.map(control => (
                <tr key={control.id}>
                  <td className="px-6 py-4">{control.frameworkName}</td>
                  <td className="px-6 py-4">{control.cwe}</td>
                  <td className="px-6 py-4">{control.controlName}</td>
                  <td className="px-6 py-4">{control.scanner}</td>
                  <td className="px-6 py-4 flex items-center gap-2">{getStatusIcon(control.status)} {control.status}</td>
                  {/* <td className="px-6 py-4">{control.priority}</td> */}
                   <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                       control.priority === 'Critical' ? 'bg-red-100 text-red-800' :
                        control.priority === 'High' ? 'bg-orange-100 text-orange-800' :
                        control.priority === 'Medium' ? 'bg-yellow-100 text-yellow-800' :
                        'bg-green-100 text-green-800'
                      }`}>
                        {control.priority}
                      </span>
                    </td>
                  <td className="px-6 py-4">{control.score}</td>
                  <td className="px-6 py-4">{control.violations}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
   </div>
  );
};

export default GovComplianceDashboard;
